import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { useAuth } from "~/context/AuthContext";
import { apiClient } from "~/lib/api-client";
import { useNavigate } from "~/lib/team-router";
import type { PitchSummary } from "~/types/PitchTypes";

export default function PitchWatcher() {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const userRef = useRef(user);
  userRef.current = user;
  const updateUserRef = useRef(updateUser);
  updateUserRef.current = updateUser;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const hasAgency = Boolean(user?.agency);

  const runningIds = (user?.teams || [])
    .filter(
      (team) =>
        team.is_pitch &&
        team.pitch_status === "running" &&
        team.creator_id === user?.id
    )
    .map((team) => team.id)
    .join(",");

  useEffect(() => {
    if (!hasAgency || !runningIds) return;
    const watched = new Set(runningIds.split(",").map(Number));

    const timer = window.setInterval(async () => {
      let pitches: PitchSummary[];
      try {
        const response = await apiClient.get("/agencies/pitches");
        pitches = response.data?.data || [];
      } catch {
        return;
      }
      const finished = pitches.filter(
        (pitch) => watched.has(pitch.id) && pitch.pitch_status !== "running"
      );
      if (finished.length === 0) return;

      updateUserRef.current({
        teams: (userRef.current?.teams || []).map((team) => {
          const match = finished.find((pitch) => pitch.id === team.id);
          return match ? { ...team, pitch_status: match.pitch_status } : team;
        }),
      });

      for (const pitch of finished) {
        const ready = pitch.pitch_status === "ready";
        const brandName = pitch.brand_name || pitch.domain;
        const open = () => navigateRef.current(`/agency/pitches/${pitch.id}`);
        const message = ready
          ? `Your pitch for ${brandName} is ready.`
          : `Your pitch for ${brandName} stopped before it finished.`;

        if ("Notification" in window && Notification.permission === "granted") {
          const notification = new Notification(
            ready ? "Zerply - pitch ready" : "Zerply - pitch stopped",
            {
              body: `${message} Click to open it.`,
              icon: "/icon.svg",
              badge: "/icon.svg",
              tag: `pitch-${pitch.id}`,
            }
          );
          notification.onclick = () => {
            window.focus();
            open();
            notification.close();
          };
        }

        const toastOptions = { action: { label: "Open", onClick: open } };
        if (ready) {
          toast.success(message, toastOptions);
        } else {
          toast.error(message, toastOptions);
        }
      }
    }, 5000);

    return () => window.clearInterval(timer);
  }, [runningIds, hasAgency]);

  return null;
}
